import React from 'react';
import PropTypes from 'prop-types';
import { Button, Table } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const RowContext = React.createContext({});

const DragHandle = () => {
  const { setActivatorNodeRef, listeners } = React.useContext(RowContext);
  return (
    <Button
      type="text"
      size="small"
      icon={<MenuOutlined />}
      ref={setActivatorNodeRef}
      style={{ cursor: 'grab' }}
      // dnd-kit requires these passthrough listeners on the drag activator.
      /* eslint-disable-next-line react/jsx-props-no-spreading */
      {...listeners}
    />
  );
};

const DraggableRow = (props) => {
  const { children, ...restProps } = props;
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: restProps['data-row-key'] });
  const style = {
    ...restProps.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: 'relative', zIndex: 999 } : {}),
  };
  const contextValue = React.useMemo(
    () => ({ setActivatorNodeRef, listeners }),
    [setActivatorNodeRef, listeners],
  );

  return (
    <RowContext.Provider value={contextValue}>
      <tr
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...restProps}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...attributes}
        ref={setNodeRef}
        style={style}
      >
        {children}
      </tr>
    </RowContext.Provider>
  );
};

DraggableRow.propTypes = {
  children: PropTypes.node,
  style: PropTypes.object,
};

DraggableRow.defaultProps = {
  children: null,
  style: {},
};

const AutoUploadRulesTable = ({
  rules, columns, isLoading, onReorder, sortable,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );
  const table = (
    <Table
      rowKey="id"
      bordered
      size="small"
      pagination={false}
      loading={isLoading}
      columns={columns}
      dataSource={rules}
      components={sortable ? { body: { row: DraggableRow } } : undefined}
      scroll={{ x: 'max-content' }}
    />
  );
  if (!sortable) return table;

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const oldIndex = rules.findIndex((rule) => rule.id === active.id);
    const newIndex = rules.findIndex((rule) => rule.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    onReorder(arrayMove(rules, oldIndex, newIndex));
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={rules.map((rule) => rule.id)} strategy={verticalListSortingStrategy}>
        {table}
      </SortableContext>
    </DndContext>
  );
};

AutoUploadRulesTable.propTypes = {
  rules: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onReorder: PropTypes.func,
  sortable: PropTypes.bool,
};

AutoUploadRulesTable.defaultProps = {
  onReorder: () => {},
  sortable: false,
};

export { DragHandle };
export default AutoUploadRulesTable;
